import {
    ReactElement,
    RefCallback,
    useCallback,
    useEffect,
    useState,
    RefObject,
    RefAttributes,
    forwardRef,
} from 'react';

import { useDebounce } from '@hh.ru/magritte-common-func-utils';
import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { SearchInput, SearchInputProps } from '@hh.ru/magritte-ui-input';
import { useAnimationTimeout } from '@hh.ru/magritte-ui-tree-selector/useAnimationTimeout';

type WrapperInputProps = SearchInputProps & {
    value: string;
    onChange: (newValue: string) => void;
    parentRef: RefCallback<HTMLInputElement>;
};

const WrapperInput = forwardRef<HTMLInputElement, WrapperInputProps>(({ parentRef, ...props }, ref) => {
    const multiRef = useMultipleRefs(ref, parentRef);
    return <SearchInput {...props} ref={multiRef} data-qa="tree-selector-search-input" />;
});

WrapperInput.displayName = 'TreeSelectorInput';

export interface UseRenderInputHook {
    (inputRef: RefObject<HTMLInputElement>): {
        contentFilterQuery: string;
        setInputValue: (newValue: string) => void;
        handleChangeInput: (newValue: string) => void;
        renderInput: (props?: SearchInputProps & RefAttributes<HTMLInputElement>) => ReactElement;
    };
}

export const useRenderInput: UseRenderInputHook = (inputRef) => {
    const [contentFilterQuery, setContentFilterQuery] = useState('');
    const { animationTimeout } = useAnimationTimeout();
    const [value, setValue] = useState('');
    const { isMobile } = useBreakpoint();

    const inputRefCallback: RefCallback<HTMLInputElement> = useCallback((node) => {
        if (!node) {
            setValue('');
            setContentFilterQuery('');
        }
    }, []);

    const ref = useMultipleRefs(inputRef, inputRefCallback);

    useEffect(() => {
        setValue('');
        setContentFilterQuery('');
    }, [isMobile]);

    const debouncedContentFilterQuery = useDebounce(
        useCallback((newValue: string) => {
            setContentFilterQuery(newValue);
        }, []),
        animationTimeout
    );

    const handleChangeInput = useCallback(
        (newValue: string) => {
            debouncedContentFilterQuery(newValue);
            setValue(newValue);
        },
        [debouncedContentFilterQuery]
    );

    const setInputValue = useCallback(
        (newValue: string) => {
            setValue(newValue);
        },
        [setValue]
    );

    const renderInput = useCallback(
        (props?: SearchInputProps & RefAttributes<HTMLInputElement>) => {
            return <WrapperInput {...props} value={value} onChange={handleChangeInput} parentRef={ref} />;
        },
        [ref, value, handleChangeInput]
    );

    return { contentFilterQuery, setInputValue, handleChangeInput, renderInput };
};
